/**
 * Strings: the mobile app's Hindi / English set (apps/mobile/src/i18n.ts, reused as-is so both
 * apps use the same words) plus what only the web app has. Hindi is the default.
 */
import { STRINGS as MOBILE, formatTime } from "@mobile/i18n";
import type { SosCategory } from "@npac/rules";

export { formatTime };

const fieldHi = {
  appName: "इलेक्शन मैनेज इंजन",
  appBy: "N-PAC · आपकी राजनीतिक सफलता की कुंजी",
  loginId: "ईमेल या मोबाइल",
  loginIdHint: "जैसे name@npac.in या 98XXXXXXXX",
  loginWrong: "ईमेल / मोबाइल या पासवर्ड गलत है",
  loginDisabled: "यह लॉगिन बंद कर दिया गया है। अपने एडमिन से बात करें।",
  loginInvalid: "सही ईमेल या 10 अंकों का मोबाइल नंबर लिखें",
  loginForAssembly: (name: string) => `विधानसभा: ${name}`,
  loginAutoAssembly: "लॉगिन करते ही आपकी विधानसभा और आपके काम अपने-आप खुलेंगे।",
  otpTitle: "OTP डालें",
  otpSentTo: (to: string, channel: string): string => `${channel} पर ${to} नंबर पर 6 अंकों का कोड भेजा गया है (5 मिनट तक मान्य)।`,
  otpCode: "कोड",
  otpVerify: "कोड जाँचें →",
  otpResend: "कोड दोबारा भेजें",
  otpResendIn: (s: number): string => `दोबारा भेजें (${s} सेकंड)`,
  otpDev: "डेव (लोकल)",
  otpDevHint: "लोकल सर्वर — कोड:",
  linkMismatch: (link: string, own: string) => `यह लिंक ${link} का है, लेकिन इस फोन पर ${own} का लॉगिन है, इसलिए ${own} खुला है। ${link} के लिए उसका लॉगिन चाहिए।`,
  logoutOther: "लॉग आउट करें",
  sessionInfo: "लॉगिन 48 घंटे चलता है — मतदान से एक दिन पहले लॉगिन करें।",

  tabBooths: "बूथ",
  tabPolling: "मतदान दिवस",
  tabSync: "सिंक",
  changeAssembly: "विधानसभा बदलें",
  showAll: "सभी बूथ दिखाएं",
  showMine: "सिर्फ मेरे बूथ",
  oneTab: "यह ऐप दूसरे टैब / विंडो में पहले से खुला है। वहीं इस्तेमाल करें, या उसे बंद करके यह पेज फिर से खोलें।",
  storageWarn: "फोन की स्टोरेज में सेव नहीं हो पाया। जगह खाली करें; ऐप बंद न करें।",
  privateMode: "इस ब्राउज़र में डेटा सेव नहीं हो सकता (प्राइवेट / इनकॉग्निटो मोड?)। सामान्य मोड में खोलें।",
  updateReady: "ऐप का नया वर्ज़न तैयार है",
  updateNow: "अपडेट करें",
  offlineBar: "ऑफलाइन — सब कुछ फोन में सेव हो रहा है",

  // install hint
  installTitle: "ऐप को होम स्क्रीन पर लगाएं",
  installWhy: "एक बार लगाने के बाद यह ऐप की तरह खुलेगा, बिना नेटवर्क के भी।",
  installAndroid: "Android (Chrome): ऊपर ⋮ मेनू → “होम स्क्रीन में जोड़ें” या “ऐप इंस्टॉल करें”।",
  installIphone: "iPhone (Safari): नीचे शेयर बटन □↑ → “होम स्क्रीन में जोड़ें” (Add to Home Screen)।",
  installButton: "ऐप इंस्टॉल करें",
  installDismiss: "बाद में / समझ गया",

  // location (web)
  locNeedsHttps: "लोकेशन के लिए ऐप https:// लिंक से खोलें।",
  locUnavailable: "इस फोन / ब्राउज़र में GPS उपलब्ध नहीं है।",
  locPermissionWeb: "लोकेशन की अनुमति नहीं मिली। ब्राउज़र में 🔒 → साइट सेटिंग → लोकेशन → अनुमति दें, फिर “फिर से ढूंढें” दबाएं।",

  // SOS
  sosTitle: "SOS — तुरंत मदद",
  sosBooth: "बूथ",
  sosCategory: "क्या हो रहा है?",
  cat_fake_voting: "फर्जी मतदान",
  cat_evm_fault: "EVM खराब",
  cat_violence: "हिंसा / झगड़ा",
  cat_other: "अन्य",
  sosNote: "छोटा विवरण (वैकल्पिक)",
  sosSend: "SOS भेजें",
  sosSending: "भेज रहे हैं…",
  sosSent: (id: string, time: string) => `✓ SOS भेजा गया (#${id}, ${time})`,
  sosSentDup: "✓ वॉर रूम तक पहुंच चुका है",
  sosWaiting: "⏳ नेटवर्क का इंतज़ार — नेटवर्क आते ही अपने आप भेजा जाएगा",
  sosFailed: "✗ SOS नहीं गया:",
  sosNew: "नया SOS",
  sosGps: (m: number) => `📍 GPS जुड़ेगा (±${m} मी)`,
  sosGpsNone: "📍 GPS नहीं मिला — SOS फिर भी जाएगा",
  sosChooseCategory: "पहले ऊपर से चुनें क्या हो रहा है",
  sosByIdLabel: "आईडी",
  smsButton: "SMS से भेजें",
  smsExplain: "SMS ऐप में संदेश तैयार मिलेगा — वहां “Send / भेजें” ज़रूर दबाएं। नेटवर्क आने पर ऐप भी अपने आप भेजेगा (दो बार गिना नहीं जाएगा)।",
  recentSos: "पिछले SOS",
  boothNotAssigned: (code: string) => `बूथ ${code} आपको नहीं दिया गया है`,
  boothUnknown: (code: string) => `बूथ ${code} सूची में नहीं है`,

  // turnout
  turnoutTitle: "मतदान प्रतिशत (टर्नआउट)",
  turnoutSlot: "समय",
  votesPolled: "अब तक पड़े वोट",
  turnoutSend: "टर्नआउट भेजें",
  turnoutPct: (p: string) => `≈ ${p}% मतदान`,
  turnoutTotal: (n: number) => `कुल मतदाता: ${n}`,
  turnoutNoTotal: "कुल मतदाता अभी पता नहीं (भाग 4 में भरें) — वोट सेव होंगे, % बाद में दिखेगा",
  turnoutBadNumber: "वोट की सही संख्या लिखें",
  turnoutQueued: "फोन में सेव — नेटवर्क आते ही भेजेंगे",
  turnoutSent: "✓ भेजा गया",
  turnoutToday: "आज भेजे गए",
  kindSos: "SOS",
  kindTurnout: "टर्नआउट",

  // my SOS (war room's view, GET /sos/mine)
  mySosTitle: "मेरे SOS — वॉर रूम में स्थिति",
  mySosEmpty: "पिछले 3 दिन में आपका कोई SOS नहीं",
  mySosAsOf: (time: string) => `स्थिति: ${time} तक की`,
  mySosOffline: "नेटवर्क नहीं — यह पिछली जानकारी है",
  sosSt_open: "खुला — वॉर रूम ने अभी नहीं देखा",
  sosSt_acknowledged: "देख लिया गया — कार्रवाई हो रही है",
  sosSt_resolved: "हल हो गया",
  sosSt_waiting: "फोन में है — नेटवर्क आते ही जाएगा",
  sosAckBy: (who: string, when: string) => `देखा: ${who}, ${when}`,
  sosResolvedBy: (who: string, when: string) => `हल किया: ${who}, ${when}`,
  sosResolutionNote: (note: string) => `टिप्पणी: ${note}`,
  sosRaisedAt: (when: string) => `भेजा: ${when}`,
  sosLocationIgnored: (boothKnown: boolean) =>
    `आपके फोन की GPS जगह भारत के बाहर / गलत थी, इसलिए वह नहीं जोड़ी गई। SOS पहुंच गया है${boothKnown ? " — बूथ की सेव की हुई जगह इस्तेमाल होगी।" : "। वॉर रूम को बूथ नंबर से जगह पता चलेगी।"}`,
  mySosShowAll: (n: number) => `सभी ${n} दिखाएं`,
  turnoutWarnNoTotal: "वोट सेव हो गए, पर % नहीं निकला: बूथ के कुल मतदाता अभी नहीं भरे (भाग 4)। भरते ही % दिखेगा।",
  turnoutWarnOverTotal: "वोट सेव हो गए, पर % नहीं निकला: वोट कुल मतदाता से ज़्यादा हैं। भाग 4 में कुल मतदाता जांचें।",
  turnoutOverTotalLocal: (n: number) => `ध्यान दें: यह संख्या कुल मतदाता (${n}) से ज़्यादा है। फिर भी भेजी जाएगी — भाग 4 में कुल मतदाता जांचें।`,

  // polling-day worker login (mobile + D-Day passcode)
  modeSurveyor: "सर्वेयर लॉगिन",
  modeWorker: "मतदान दिवस — कार्यकर्ता / एजेंट",
  ddayTitle: "मतदान दिवस लॉगिन",
  ddayExplain: "वॉर रूम के लिंक से खुला है — नंबर या पासकोड की ज़रूरत नहीं। बस अपना बूथ चुनें।",
  ddayNoLink: "मतदान दिवस के लिए वॉर रूम से मिला लिंक (WhatsApp पर) खोलें — उसी से आपका बूथ खुलेगा।",
  ddayHow: "लिंक कैसे बनेगा: वेब डैशबोर्ड → वॉर-रूम → “मतदान दिवस लिंक” → “नया लिंक बनाएं” → WhatsApp पर भेजें। कार्यकर्ता उस लिंक पर टैप करे — यह स्क्रीन अपने-आप खुल जाएगी, कोई पासवर्ड नहीं।",
  ddayPasteLabel: "या वॉर-रूम का लिंक यहाँ पेस्ट करें",
  ddayPasteOpen: "लिंक खोलें",
  ddayPasteBad: "यह मतदान दिवस का लिंक नहीं है — वॉर-रूम से मिला पूरा लिंक डालें (उसमें dday=1 होता है)।",
  ddaySurveyorHint: "सर्वेयर / एडमिन: ऊपर “सर्वेयर लॉगिन” से अपने ID-पासवर्ड से लॉगिन करें — नीचे “मतदान दिवस” बटन मिल जाएगा।",
  ddayPickBooth: "अपना बूथ चुनें",
  ddaySearch: "बूथ नंबर या नाम लिखें",
  ddayNoMatch: "इस नंबर / नाम का कोई बूथ नहीं मिला",
  ddayMore: (n: number) => `और ${n} बूथ — ऊपर नंबर लिखकर खोजें`,
  ddayName: "आपका नाम (वैकल्पिक)",
  ddayMobileOpt: "मोबाइल नंबर (वैकल्पिक)",
  ddayMobileBad: "10 अंकों का मोबाइल नंबर लिखें, या खाली छोड़ें",
  ddayStart: (code: string) => `बूथ ${code} के लिए शुरू करें`,
  ddayLoadingBooths: "बूथ सूची आ रही है…",
  ddayChange: "बदलें",
  ddayRetry: "फिर से कोशिश करें",
  ddayOpening: "मतदान दिवस — SOS",
  ddayOpeningSub: "खुल रहा है…",
  ddayAssembly: "विधानसभा कोड",
  ddayAssemblyHint: "जैसे HAR_01 (वॉर रूम के लिंक में होता है)",
  ddayMobile: "आपका मोबाइल नंबर",
  ddayPasscode: "मतदान दिवस पासकोड",
  ddayLogin: "लॉगिन करें",
  ddayClosed: "यह मतदान दिवस लिंक अब चालू नहीं है। वॉर रूम से नया लिंक लें।",
  ddayNotOnList: "यह नंबर किसी बूथ की कार्यकर्ता या एजेंट सूची में नहीं है। अपने बूथ अध्यक्ष से बात करें।",
  ddayWrong: "पासकोड गलत है",
  workerMyBooths: "आपके बूथ",
  workerAnyBooth: "नीचे बूथ नंबर लिखें — विधानसभा के किसी भी बूथ से SOS भेज सकते हैं।",
  workerHello: (name: string) => `नमस्ते ${name} — बूथ पर कोई गड़बड़ी हो तो यहीं से SOS भेजें। जवाब नीचे दिखेगा।`,
  // SOS photo
  photoAdd: "📷 फोटो जोड़ें (वैकल्पिक)",
  photoChange: "फोटो बदलें",
  photoRemove: "फोटो हटाएं",
  photoBusy: "फोटो छोटी की जा रही है…",
  photoFailed: "यह फोटो नहीं जुड़ पाई। दूसरी फोटो चुनें या बिना फोटो भेजें।",
  photoAttached: (kb: number) => `फोटो जुड़ी (${kb} KB)`,
  // our party's votes in turnout
  ourVotesLabel: "हमारी पार्टी के वोट (अनुमान, वैकल्पिक)",
  ourVotesHint: "एजेंट की अपनी गिनती; कुल पड़े वोट से ज़्यादा नहीं",
  ourVotesBad: "हमारे वोट कुल पड़े वोट से ज़्यादा नहीं हो सकते",
  ourVotesPct: (pct: string) => `पड़े वोटों में हमारा हिस्सा ${pct}%`,
  // round 4: duties, open name lists, survey, past result, campaign
  addProblem: "और समस्या लिखें",
  addSuggestion: "और सुझाव लिखें",
  secSentiment: "6. बूथ की स्थिति — सर्वेयर का अनुमान (%)",
  secSentimentHint: "यह बूथ का कुल अनुमान है (तीनों का योग 100%)। लोगों से एक-एक करके वर्तमान सर्वे (नाम, मोबाइल, समस्या, सुझाव) नीचे \"सर्वे\" टैब में भरें।",
  svOpenPerson: "👤 व्यक्ति-वार वर्तमान सर्वे भरें",
  secResult: "पिछले चुनाव का विवरण (बूथ के वोट)",
  list_workers: "7. पार्टी कार्यकर्ताओं की सूची",
  list_influencers: "8. प्रभावी / प्रबुद्ध व्यक्तियों की सूची",
  list_public: "9. सक्रिय आमजनों की सूची",
  namesCount: (n: number) => (n ? `${n} नाम` : "अभी कोई नाम नहीं"),
  unsentNames: "फोन में सेव — नेटवर्क मिलते ही भेजा जाएगा",
  myDuties: "आपकी जवाबदेही",
  noBoothDuty: "आपकी जवाबदेही में बूथ का कोई काम नहीं है। नीचे “अभियान” खोलें।",
  // people lists
  pAdd: "＋ नया नाम जोड़ें",
  pName: "नाम *",
  pRole: "दायित्व *",
  pCaste: "जाति",
  pMobile: "मोबाइल नंबर *",
  pLeaning: "राजनीतिक विचारधारा *",
  pNote: "पहचान / पेशा",
  pNotePh: "जैसे सेवानिवृत्त शिक्षक, प्रधान",
  lean_supporter: "हमारे समर्थक",
  lean_inclined: "हमारी ओर झुकाव",
  lean_neutral: "तटस्थ",
  lean_opponent: "विरोधी विचारधारा",
  pSaveNew: "सूची में जोड़ें",
  pSaveEdit: "बदलाव सेव करें",
  pCancel: "रद्द करें",
  pEdit: "बदलें",
  pRemove: "हटाएं",
  pConfirmRemove: (name: string) => `${name} को सूची से हटाएं?`,
  pSearch: "नाम या नंबर से खोजें",
  pEmpty: "अभी कोई नाम नहीं। ऊपर “नया नाम जोड़ें” दबाएं।",
  pDup: "यह मोबाइल नंबर इस सूची में पहले से है",
  pFix: "लाल खाने ठीक करें",
  pSavedOnline: "✓ सर्वर पर सेव हो गया",
  pSavedOffline: "✓ फोन में सेव — नेटवर्क मिलते ही भेजा जाएगा",
  pNotPrinted: "जितने चाहें उतने नाम। ये बूथ बुक में नहीं छपते; वेब से Excel में निकाले जा सकते हैं।",
  pShown: (shown: number, all: number) => `${all} में से ${shown} दिख रहे हैं`,
  pShowMore: "और दिखाएं",
  // survey
  svExplain: "बूथ के लोग किस तरफ हैं — फोन या भौतिक सर्वे से। तीनों का योग 100% हो।",
  svStrong: "मजबूत (हमारे पक्ष में) %",
  svNeutral: "ठीक-ठाक स्थिति %",
  svWeak: "ज्यादा मेहनत की जरूरत %",
  svSample: "कितने लोगों से बात हुई",
  svMethod: "सर्वे का तरीका",
  m_call: "फोन",
  m_ground: "भौतिक (घर-घर)",
  m_mixed: "दोनों",
  svDate: "सर्वे की तारीख",
  svSum: (n: number) => `योग: ${n}%`,
  svSumBad: "तीनों का योग 100% होना चाहिए",
  svSave: "सर्वे सेव करें",
  // counts only, add only (round 5)
  aoNote: "पहले से भरी जानकारी फोन पर नहीं दिखती — केवल संख्या। आप यहाँ से नया जोड़ सकते हैं; देखना और सुधारना वेब डैशबोर्ड पर होता है।",
  aoOnServer: "सर्वर पर पहले से",
  aoFilled: "✓ पहले से भरा है",
  aoEmpty: "खाली",
  aoFilledShort: "भरे",
  aoProblems: "समस्याएँ",
  aoSuggestions: "सुझाव",
  aoFilledField: (label: string) => `${label}: ✓ पहले से भरा है`,
  aoNothingNew: "कुछ नया भरें — पहले से भरी चीज़ें दोबारा नहीं भेजनी हैं",
  aoAdded: (added: number, skipped: number) => `✓ ${added} नया जुड़ा` + (skipped ? ` · ${skipped} पहले से था, नहीं बदला` : ""),
  aoMembersLabel: "सदस्य सर्वर पर जुड़े हैं",
  aoNewMember: (i: number) => `नया सदस्य ${i}`,
  aoMembersFull: "20 सदस्य पूरे हैं। बदलाव वेब डैशबोर्ड पर करें।",
  aoLines: (label: string, n: number) => `${label}: सर्वर पर ${n}`,
  aoLinesFull: "10 पूरे हैं",
  aoDemoLocked: "मतदाता विवरण भरा जा चुका है। सुधार वेब डैशबोर्ड पर।",
  aoCastes: (n: number) => `जातियाँ: ${n}`,
  aoPeopleLabel: "नाम सर्वर पर जुड़े हैं",
  aoPending: (n: number) => `${n} नाम फोन में — नेटवर्क मिलते ही जाएँगे`,
  aoAddedName: (name: string) => `✓ “${name}” जोड़ दिया`,
  aoSurveyDone: "✓ इस बूथ का सर्वे भरा जा चुका है",
  aoSurveyNew: "नया सर्वे भरें",
  aoSurveyNewNote: "नया सर्वे पुराने की जगह लेगा।",
  aoResultDone: "✓ इस बूथ का पिछला चुनाव परिणाम भरा जा चुका है। सुधार वेब डैशबोर्ड पर।",
  aoNamesCount: (n: number) => (n ? `${n} नाम` : "अभी कोई नाम नहीं"),
  // driver duty (GPS), round 5
  drvOpening: "गाड़ी का ड्राइवर लिंक खुल रहा है…",
  drvClosed: "यह ड्राइवर लिंक अब चालू नहीं है। नया लिंक मंगाएँ।",
  drvExit: "इस गाड़ी से बाहर निकलें (लिंक हटाएँ)",
  drvExitHelp: "ड्यूटी ख़त्म करके बाहर निकलें — फिर यह फ़ोन दूसरी गाड़ी के लिंक या अपने लॉगिन से खुल सकेगा।",
  drvExitConfirm: "गाड़ी का लिंक इस फ़ोन से हटा दें? (चल रही ड्यूटी पहले ख़त्म करें)",
  modeDriver: "ड्राइवर",
  drvPasteTitle: "प्रचार गाड़ी का ड्राइवर",
  drvPasteHow: "सिस्टम (प्रचार गाड़ी › गाड़ी › नया ड्राइवर लिंक) से मिला लिंक यहाँ डालकर सेव करें — ड्यूटी स्क्रीन खुल जाएगी और GPS रिकॉर्ड शुरू हो जाएगा।",
  drvPasteLabel: "ड्राइवर लिंक",
  drvPasteOpen: "सेव करें और शुरू करें",
  drvPasteBad: "यह ड्राइवर लिंक नहीं है — गाड़ी के रिकॉर्ड से बना पूरा लिंक डालें (उसमें drive= होता है)।",
  drvNoLink: "ड्राइवर के लिए अपनी गाड़ी का लिंक खोलें (प्रचार गाड़ी पेज से भेजा गया)।",
  drvTitle: "गाड़ी की ड्यूटी",
  drvCandidate: "प्रत्याशी की गाड़ी",
  drvStart: "▶ ड्यूटी शुरू करें",
  drvEnd: "■ ड्यूटी बंद करें",
  drvEndConfirm: "ड्यूटी बंद करें? इसके बाद GPS दर्ज नहीं होगा।",
  drvOn: "ड्यूटी चालू है",
  drvOff: "ड्यूटी बंद है",
  drvSince: (t: string) => `${t} से`,
  drvKm: "किमी (आज की ड्यूटी)",
  drvSent: "GPS भेजे",
  drvQueued: (n: number) => `${n} लोकेशन फोन में — नेटवर्क मिलते ही जाएँगी`,
  drvGps: (acc: number, ago: string) => `GPS ±${acc} मी · ${ago}`,
  drvWaitingGps: "GPS ढूँढ रहे हैं… खुले आसमान में रहें",
  drvDenied: "लोकेशन की अनुमति नहीं मिली। फोन की सेटिंग → ब्राउज़र / ऐप → लोकेशन → 'अनुमति दें' चुनें, फिर यह पेज दोबारा खोलें।",
  drvUnavailable: "इस फोन पर GPS नहीं मिल रहा। लोकेशन (GPS) चालू करें।",
  drvKeepOpen: "ड्यूटी के दौरान यह ऐप खुला रखें और स्क्रीन बंद न करें — कई फोन बंद स्क्रीन में GPS रोक देते हैं। फोन चार्जिंग पर रखें।",
  drvPrivacy: "GPS सिर्फ़ ड्यूटी के दौरान दर्ज होता है — गाड़ी का रास्ता, किलोमीटर और कौन-से बाज़ार / चौराहे पहुँचे। ड्यूटी बंद करते ही ट्रैकिंग बंद।",
  drvToday: (km: number, places: number, booths: number) => `आज: ${km} किमी · ${places} जगहें · ${booths} बूथ`,
  drvPlan: "आज इन जगहों पर जाना है",
  drvPlanNone: "आज का रूट तय नहीं हुआ — जहाँ भेजा जाए वहाँ जाएँ।",
  drvReached: "पहुँचे",
  drvJustReached: (name: string) => `✓ पहुँचे: ${name}`,
  drvScreenOn: "स्क्रीन चालू रखी गई है",
  drvBackgroundOk: "GPS बैकग्राउंड में चल रहा है — फोन लॉक कर सकते हैं (ऊपर नोटिफिकेशन दिखेगा)। फोन चार्जिंग पर रखें, मोबाइल डेटा चालू रखें।",
  drvNotifTitle: "प्रचार गाड़ी — ड्यूटी चालू",
  drvNotifText: "GPS दर्ज हो रहा है। ड्यूटी बंद करने के लिए ऐप खोलें।",
  drvDeniedApp: "लोकेशन की अनुमति नहीं मिली। सेटिंग में 'हमेशा अनुमति दें' (Allow all the time) चुनें, फिर ड्यूटी फिर शुरू करें।",
  drvOpenSettings: "सेटिंग खोलें",
  drvMin: (m: number) => (m < 60 ? `${m} मिनट` : `${Math.floor(m / 60)} घंटा ${m % 60} मिनट`),
  // past result
  resExplain: (year: number) => `${year} के चुनाव में इस बूथ पर हर प्रत्याशी के वोट (Form-20 से)।`,
  resOthers: "अन्य (बाकी प्रत्याशी)",
  resTotal: "कुल",
  resSave: "वोट सेव करें",
  resSaved: "✓ सर्वर पर सेव हो गया",
  tabExit: "एग्ज़िट पोल",
  epTitle: "एग्ज़िट पोल सर्वे",
  epRules: "वोट डालकर निकले मतदाता से उसकी मर्ज़ी हो तो ही पूछें। नाम / फ़ोन नंबर न लें। नतीजे किसी को न बताएँ, न सोशल मीडिया / व्हाट्सऐप पर डालें — यह धारा 126A के तहत अपराध है।",
  epSavedNow: (n: number) => `अभी ${n} सेव`,
  epToday: (n: number) => `आज सर्वर पर ${n}`,
  epQueued: (n: number) => `${n} उत्तर फ़ोन में रुके — नेटवर्क आते ही जाएँगे`,
  epAllSent: "✓ सब भेज दिए",
  epNoPlan: "वॉर रूम ने अभी सर्वे पद्धति तय नहीं की — ये शुरुआती विकल्प हैं।",
  epBooth: "किस बूथ पर सर्वे",
  epPickBooth: "बूथ चुनें",
  epChoice: "किसे वोट दिया?",
  epAge: "उम्र",
  epAges: { a18_25: "18-25", a26_40: "26-40", a41_60: "41-60", a60plus: "60+" } as Record<string, string>,
  epGender: "लिंग",
  epGenders: { M: "पुरुष", F: "महिला", O: "अन्य" } as Record<string, string>,
  epMore: "और सवाल (वैकल्पिक)",
  epPrev: "पिछली बार किसे वोट दिया था?",
  epFirstTime: "पहली बार वोट",
  epIssue: "सबसे बड़ा मुद्दा",
  epMethod: "कैसे पूछा",
  epMethods: { face: "आमने-सामने", phone: "फ़ोन पर" } as Record<string, string>,
  epSave: "सेव करें — अगला मतदाता",
  epNeedBooth: "पहले बूथ चुनें",
  epGps: (m: number) => `📍 लोकेशन मिल रही है (±${m} मी.)`,
  epNoGps: "📍 लोकेशन नहीं मिली — उत्तर फिर भी सेव होगा",
  bsTitle: "बस्ती और भीड़ वाली जगहें",
  bsCardSub: "मलिन / दलित / आदिवासी बस्ती, घनी आबादी, बाज़ार, चौराहा — पता और आबादी के साथ",
  bsHelp: "इस बूथ की हर बस्ती और भीड़ वाली जगह जोड़ें। सबसे ज़्यादा आबादी वाली बस्ती ★ पीले रंग में दिखेगी।",
  bsAdd: "+ नई बस्ती / जगह जोड़ें",
  bsNone: "अभी कोई बस्ती या जगह नहीं जुड़ी।",
  bsKind: "किस तरह की जगह",
  bsName: "बस्ती / जगह का नाम",
  bsNamePh: "जैसे: कच्ची बस्ती, हरिजन टोला, पुराना मोहल्ला",
  bsAddress: "पता",
  bsAddressPh: "गली / पहचान — जैसे: रेलवे लाइन के पास",
  bsPopulation: "अनुमानित आबादी (लोग)",
  bsHouseholds: "लगभग कितने घर",
  bsNumber: "सिर्फ़ अंक लिखें",
  bsGps: "लोकेशन",
  bsGpsAuto: "📍 अभी की लोकेशन ली",
  bsGpsFinding: "लोकेशन ले रहे हैं…",
  bsGpsNone: "लोकेशन नहीं मिली — बाद में भी जोड़ सकते हैं",
  bsGpsTake: "📍 यहाँ की लोकेशन लें",
  bsGpsClear: "हटाएँ",
  bsSave: "सेव करें",
  bsSaved: "✓ सेव हो गया",
  bsCancel: "रद्द करें",
  bsEdit: "बदलें",
  bsDensest: "★ सबसे घनी आबादी",
  bsPeople: (n: string) => `~${n} लोग`,
  bsHouses: (n: string) => `${n} घर`,
  bsKinds: { slum: "मलिन / गरीब बस्ती", dalit: "दलित बस्ती", tribal: "आदिवासी बस्ती", dense: "घनी आबादी क्षेत्र", village: "गाँव / मोहल्ला", market: "बाज़ार", chowk: "चौराहा", crowd: "भीड़ वाली जगह", ground: "मैदान", auditorium: "ऑडिटोरियम", hall: "हॉल", other: "अन्य" } as Record<string, string>,
  resNeedsNet: "यह हिस्सा सीधे सर्वर पर सेव होता है — नेटवर्क चाहिए।",
  resNoElection: "पिछला चुनाव अभी नहीं जुड़ा। एडमिन से कहें: वेब पर “डेटा इम्पोर्ट” में प्रत्याशी सूची बनाएं।",
  resNothing: "कम से कम एक प्रत्याशी के वोट लिखें",
  // leaderboard: this surveyor's points
  mpPoints: (n: number) => `इस हफ़्ते आपके अंक: ${n.toLocaleString("en-IN")}`,
  mpRank: (r: number, of: number) => `टीम में ${r}वाँ स्थान (${of} में से)`,
  mpStart: "काम सेव करें — अंक जुड़ेंगे",
  // bottom tabs, one per duty (N-PAC, 31 Oct 2026)
  tabPlaces: "सभी जगहें",
  tabTour: "भ्रमण",
  tabRallies: "रैली / नुक्कड़",
  tabVehicles: "प्रचार गाड़ी",
  vStaff: "कर्मचारी — काम और डाटा",
  vDriverOpt: "ड्राइवर — लिंक डालें",
  vDriverConfirm: "यह फ़ोन अब गाड़ी के ड्राइवर मोड में चलेगा (आपका यह लॉगिन बंद हो जाएगा)। आगे बढ़ें?",
  vDriverNote: "ड्राइवर के फ़ोन पर ही डालें: सेव करते ही यह फ़ोन उस गाड़ी की ड्यूटी स्क्रीन में चला जाता है और GPS रिकॉर्ड शुरू हो जाता है। लिंक गाड़ी के रिकॉर्ड (वेब › प्रचार गाड़ी › गाड़ी › नया ड्राइवर लिंक) से बनता है।",
  // विधानसभा की सभी जगहें
  apTitle: "विधानसभा की सभी जगहें",
  apExplain: "हर बूथ के बाज़ार, चौराहे, भीड़ वाली जगहें और बस्तियाँ — GPS के साथ। किसी बूथ पर टैप करके जगह जोड़ें या बदलें।",
  apSummary: (n: string, gps: string, b: string, all: string) => `कुल ${n} जगहें · ${gps} GPS के साथ · ${b} / ${all} बूथ में`,
  apSearch: "बूथ नंबर, बूथ या जगह का नाम",
  apAdd: "नई जगह जोड़ें — बूथ नंबर",
  apOpen: "खोलें",
  apNoBooth: "इस नंबर का बूथ नहीं मिला",
  apNoGps: "GPS नहीं",
  apOnlyNoGps: (n: string) => `सिर्फ़ वे जगहें दिखाएँ जिनकी GPS बाकी है (${n}) — वहाँ जाकर “यहाँ की लोकेशन लें” दबाएँ`,
  apEmptyBooths: (n: string) => `बिना जगह वाले बूथ (${n})`,
  apNone: "कोई जगह नहीं मिली",
  // campaign
  tabCampaign: "अभियान",
  campTitle: "चुनाव अभियान",
  campExplain: "आपकी जवाबदेही के अभियान के काम। यह हिस्सा नेटवर्क पर चलता है।",
  camp_rallies: "रैली, नुक्कड़ सभा/नाटक मैनेजमेंट",
  camp_tour: "प्रत्याशी भ्रमण मैनेजमेंट",
  camp_vehicles: "प्रचार गाड़ी मैनेजमेंट",
  campSub_rallies: "अनुमति, तैयारी की सूची, भीड़ और खर्च",
  campSub_tour: "आज के पड़ाव — पहुँचे / देरी / छूटा",
  campSub_vehicles: "हर गाड़ी का आज का रूट, किलोमीटर, ईंधन",
  netNeeded: "नेटवर्क नहीं मिला। नेटवर्क आने पर फिर खोलें।",
  reload: "फिर से लोड करें",
  saved: "✓ सेव हो गया",
  save: "सेव करें",
  required: "यह भरना ज़रूरी है",
  boothCodeBad: "बूथ नंबर ऐसे लिखें: 12 या 45A",
  phoneBad: "10 अंकों का मोबाइल नंबर लिखें",
  rUpcoming: "आने वाले",
  rSearch: "🔍 कार्यक्रम खोजें — नाम, जगह, बूथ, पंचायत, अतिथि",
  rNoMatch: "इस नाम का कोई कार्यक्रम नहीं मिला।",
  rPast: "हो चुके",
  rNew: "＋ नया कार्यक्रम",
  rEdit: "कार्यक्रम बदलें",
  rTitle: "कार्यक्रम का नाम *",
  rKind: "प्रकार",
  rDate: "तारीख *",
  rTime: "समय *",
  rPlace: "स्थान *",
  rBooth: "बूथ नंबर (अगर हो)",
  rExpected: "अनुमानित भीड़",
  rActual: "असल भीड़",
  rCost: "खर्च (₹)",
  rIncharge: "प्रभारी",
  rInchargePhone: "प्रभारी का मोबाइल",
  rStatus: "स्थिति",
  rChecklist: "तैयारी की सूची",
  rChecklistN: (d: number, n: number) => `तैयारी ${d} / ${n}`,
  rNotes: "नोट",
  rEmpty: "इस सूची में कोई कार्यक्रम नहीं।",
  tDay: "दिन",
  tToday: "आज",
  tAdd: "＋ पड़ाव जोड़ें",
  tEdit: "पड़ाव बदलें",
  tPlace: "स्थान / गाँव *",
  tTime: "समय",
  tSeq: "क्रम",
  tBooth: "बूथ नंबर",
  tPurpose: "उद्देश्य",
  tContact: "स्थानीय संपर्क",
  tContactPhone: "संपर्क का मोबाइल",
  tEmpty: "इस दिन कोई पड़ाव तय नहीं।",
  tRoute: "Google Map पर रास्ता",
  vEmpty: "अभी कोई गाड़ी नहीं जुड़ी — एडमिन वेब से जोड़ें।",
  vToday: "आज का रूट",
  vNoTrip: "आज का रूट नहीं भरा",
  vSetTrip: "आज का रूट भरें",
  vEditTrip: "रूट बदलें",
  vArea: "इलाका / गाँव *",
  vBooths: "बूथ नंबर",
  vBoothsHint: "कॉमा से अलग: 12, 13, 45A",
  vStartKm: "शुरू का मीटर (किमी)",
  vEndKm: "शाम का मीटर (किमी)",
  vKmBad: "शाम का मीटर सुबह वाले से कम नहीं हो सकता",
  vFuelL: "ईंधन (लीटर)",
  vFuelCost: "ईंधन ₹",
  vCash: "ड्राइवर को रुपए दिए ₹",
  vCashHint: "आज नकद कितना दिया (डीज़ल / खर्च के लिए)",
  vDriver: "ड्राइवर",
  vKm: (n: number) => `${n} किमी`,

  // वोट डाला (polling-day booth agent, N-PAC 31 Oct 2026)
  tabVoted: "वोट डाला",
  vtTitle: "वोट डाला — मतदान दिवस",
  vtExplain: "जो वोट डालकर आए उनका क्रम सं. लिखें या पर्ची का QR स्कैन करें। नीचे वे समर्थक हैं जो अभी नहीं आए — उन्हें घर से लाएँ।",
  vtPrivacy: "🔒 नाम सिर्फ आपके बूथ के हैं, केवल समर्थकों को वोट डालने लाने के लिए। किसी से साझा न करें, स्क्रीनशॉट न लें — हर काम दर्ज होता है।",
  vtOathTitle: "शपथपत्र",
  vtOathWhy: "मतदाता सूची के नाम खोलने से पहले हर 12 घंटे में सहमति देनी होती है।",
  vtOathAgree: "मैं सहमत हूँ",
  vtBoothPick: "अपना बूथ",
  vtBoothNo: "बूथ नंबर",
  vtNoBooth: "आपको कोई बूथ नहीं दिया गया — एडमिन से बात करें।",
  vtVoted: "वोट डाले",
  vtSupporters: "समर्थकों ने डाले",
  vtPendingN: "समर्थक बाकी",
  vtSerial: "मतदाता का क्रम सं. (भाग सूची से)",
  vtSerialPh: "जैसे 245",
  vtMark: "वोट डाला ✓",
  vtScan: "📷 पर्ची का QR स्कैन करें",
  vtScanClose: "स्कैन बंद करें",
  atTitle: "उपस्थिति (QR) — ID कार्ड स्कैनर",
  atHelp: "कार्यक्रम चुनें, फिर कार्यकर्ता के ID कार्ड का QR स्कैन करें या मोबाइल नंबर लिखें — नाम, बूथ और कुल उपस्थिति तुरंत दिखेगी। (इंटरनेट चाहिए।)",
  atEvent: "कार्यक्रम",
  atKind: (k: string): string => (k === "rally" ? "रैली / नुक्कड़" : k === "tour" ? "प्रत्याशी भ्रमण (आज)" : "बैठक / अन्य"),
  atNoRallies: "कोई रैली सूची में नहीं — वेब से रैली जोड़ें, या \"बैठक / अन्य\" चुनकर नाम लिखें।",
  atTourToday: (d: string): string => `आज (${d}) के भ्रमण की उपस्थिति`,
  atMeetingName: "बैठक का नाम",
  atMeetingPh: "जैसे: बूथ अध्यक्ष बैठक, 5 नवंबर",
  atSoFar: "उपस्थित अब तक",
  atPickEvent: "पहले कार्यक्रम चुनें",
  atScan: "📷 ID कार्ड का QR स्कैन करें",
  atPhone: "या मोबाइल नंबर लिखें",
  atName: "नाम (सूची में नहीं है, नया जोड़ें)",
  atMark: "उपस्थिति दर्ज करें",
  atMarked: (n: string): string => `✓ ${n} — उपस्थिति दर्ज`,
  atAlready: (n: string): string => `${n} की उपस्थिति पहले ही दर्ज है`,
  atNotRegistered: "यह नंबर किसी बूथ की सूची में नहीं है — नाम लिखकर दर्ज करें।",
  atList: "आज दर्ज (नया पहले)",
  rvTitle: "सभी काम की समीक्षा",
  rvOpenFull: "पूरी स्क्रीन में खोलें",
  rvNoAssembly: "विधानसभा चुनें।",
  tabAttendance: "उपस्थिति",
  tabReview: "समीक्षा",
  tabHome: "होम",
  hmHello: (n: string): string => (n ? `${n}, आपके काम:` : "आपके काम:"),
  hmCount: (n: number): string => `${n} सेक्शन`,
  hmBooths: "बूथ की जानकारी, टीम, स्थिति",
  hmSurvey: "डोर-टू-डोर / फ़ोन से जनता की राय",
  hmVehicles: "कर्मचारी: किमी, डीज़ल, खर्च · ड्राइवर: लिंक",
  hmPolling: "SOS, मतदान %, दिन की रिपोर्ट",
  hmReview: "पूरा डैशबोर्ड — सिर्फ़ देखने के लिए",
  hmExit: "मतदान के बाद मतदाताओं से पूछें",
  hmSync: "फोन का डेटा सर्वर पर भेजें",
  hmAttendance: "कार्यकर्ता ID स्कैन",
  hmAttendanceSub: "ID कार्ड का QR — रैली / सभा में उपस्थिति",
  hmPlaces: "विधानसभा की सभी जगहें",
  hmVoted: "बूथ पर किसने वोट डाला",
  hmCounting: "मतगणना के राउंड",
  hmNote: "जो काम एडमिन पैनल से आपको दिया गया है, वही यहाँ दिखता है।",
  hmLocked: "अभी लॉक है",
  hmLockedNote: "यह मॉड्यूल इस विधानसभा के लिए अभी खुला नहीं है। खुलवाने के लिए N-PAC से संपर्क करें।",
  hmLockedOk: "ठीक है",
  vtScanHint: "पर्ची का QR कोड कैमरे के सामने रखें",
  vtCameraNo: "कैमरा नहीं खुला। ब्राउज़र में कैमरे की अनुमति दें, या क्रम सं. लिखें।",
  vtCameraUnsupported: "इस फोन / ब्राउज़र में QR स्कैन नहीं हो सकता — क्रम सं. लिखें।",
  vtMarked: (serial: number, name: string, supporter: boolean) => `क्रम ${serial} — ${name}${supporter ? " (समर्थक)" : ""} ✓`,
  vtAlready: (serial: number, name: string) => `क्रम ${serial} — ${name}: पहले से दर्ज है`,
  vtUnknown: (serial: number) => `क्रम ${serial} इस बूथ की सूची में नहीं है — फिर से देखें`,
  vtQueued: (serial: number) => `क्रम ${serial} फोन में सेव — नेटवर्क आते ही भेज देंगे`,
  vtUnsent: (n: number) => `${n} निशान भेजना बाकी — नेटवर्क आते ही अपने-आप जाएँगे`,
  vtSendNow: "अभी भेजें",
  vtUndo: "↶ आख़िरी निशान हटाएँ",
  vtUndone: (serial: number) => `क्रम ${serial} का निशान हटा दिया`,
  vtUndoNeedsNet: "निशान हटाने के लिए नेटवर्क चाहिए",
  vtSerialBad: "1 से 100000 के बीच क्रम सं. लिखें",
  vtNotSlip: "यह हमारी मतदाता पर्ची का QR नहीं है",
  vtOtherAssembly: (a: string) => `यह पर्ची दूसरी विधानसभा (${a}) की है`,
  vtOtherBooth: (b: string) => `यह पर्ची बूथ ${b} की है — इस बूथ की नहीं`,
  vtPendingTitle: "जो समर्थक अभी नहीं आए",
  vtPendingNone: "सभी समर्थक वोट डाल चुके 🎉",
  vtNoSupporters: "इस बूथ में अभी कोई समर्थक चिह्नित नहीं — वेब पर मतदाता सूची में चिह्नित करें।",
  vtCame: "आया ✓",
  vtHouse: "मकान",
  vtRecent: "अभी दर्ज हुए",
  vtNoVoters: "इस बूथ की मतदाता सूची अभी नहीं चढ़ी — वेब पर चढ़ाएँ।",
  vtVia: (v: string | null): string => (v === "qr" ? "QR" : v === "list" ? "सूची" : v === "web" ? "वेब" : "क्रम सं."),
  vtNeedsNet: "नाम देखने के लिए नेटवर्क चाहिए। निशान फिर भी लगाते रहें — फोन में सेव होंगे।",

  // मतगणना (counting agent)
  tabCounting: "मतगणना",
  ctTitle: "मतगणना — बूथ के वोट",
  ctExplain: "EVM पर दिखे हर प्रत्याशी के वोट बूथ अनुसार भरें, राउंड नंबर के साथ। वॉर रूम में तुरंत बढ़त और जीत-हार का अनुमान दिखेगा।",
  ctNoSetup: "वॉर रूम ने अभी प्रत्याशियों की सूची नहीं बनाई — थोड़ी देर बाद फिर खोलें।",
  ctDate: (d: string) => `मतगणना: ${d}`,
  ctDone: (n: number, all: number) => `${n} / ${all} बूथ भेजे`,
  ctBooth: "बूथ",
  ctBoothPick: "बूथ चुनें",
  ctRound: "राउंड नंबर",
  ctRoundHint: (n: number | null) => (n ? `1 से ${n}` : "जिस राउंड में यह बूथ गिना गया"),
  ctRoundBad: (n: number | null) => (n ? `राउंड 1 से ${n} के बीच लिखें` : "सही राउंड नंबर लिखें"),
  ctVotes: "हर प्रत्याशी के वोट (न मिले हों तो 0)",
  ctTotal: "कुल वोट",
  ctSave: "वोट भेजें",
  ctSaved: (code: string, total: string, corrected: boolean) => `बूथ ${code} के वोट ${corrected ? "बदल दिए" : "भेज दिए"} ✓ (कुल ${total})`,
  ctEntered: (round: number | null, time: string) => `इस बूथ के वोट भेजे जा चुके हैं${round ? ` (राउंड ${round})` : ""} · ${time}`,
  ctResend: "आंकड़े बदलने हैं? फिर से भेजें",
  ctConfirm: "पक्का? पहले भेजे आंकड़े बदल जाएँगे (पुराने भी दर्ज रहते हैं)।",
  ctConfirmYes: "हाँ, बदलें",
  ctCancel: "रहने दें",
  ctAllRequired: "हर प्रत्याशी के वोट भरें (0 भी चलेगा)",
  ctRoundShort: (n: number) => `रा. ${n}`,

  // वर्तमान सर्वे (door to door / telecaller)
  tabSurvey: "सर्वे",
  csTitle: "वर्तमान सर्वे",
  csExplain: "हर व्यक्ति से बात करके एक फ़ॉर्म भरें — सम्मिट होते ही आपके रिकॉर्ड में जुड़ जाएगा।",
  csMethod: "सर्वे का तरीका",
  csD2d: "डोर टु डोर",
  csTele: "टेलिकॉलर",
  csBooth: "बूथ नं और नाम",
  csBoothNo: "बूथ नंबर",
  csNoBooth: "आपको अभी कोई बूथ नहीं दिया गया — एडमिन से बात करें।",
  csPlace: "मुहल्ला / गाँव का नाम",
  csPlacePick: "— चुनें —",
  csPlaceOther: "अन्य — नाम लिखें",
  csPlaceName: "मुहल्ला / गाँव का नाम लिखें",
  csPlaceNone: "इस बूथ का कोई गाँव / मोहल्ला अभी सूची में नहीं — नाम लिखें (या “विधानसभा की सभी जगहें” में जोड़ें)।",
  csPlaceOffline: "सूची के लिए नेटवर्क नहीं — नाम लिखें।",
  csName: "नाम *",
  csMobile: "मोबाइल नम्बर *",
  csAge: "उम्र",
  csProfession: "पेशा",
  csOrgRole: "राजनीतिक / सामाजिक संगठन दायित्व",
  csStatus: "प्रत्याशी स्थिति सर्वे *",
  csStatusPick: "— चुनें —",
  csStrong: "मजबूत स्थिति",
  csNeutral: "ठीक-ठाक स्थिति",
  csWeak: "ज़्यादा मेहनत की ज़रूरत",
  csProblems: "मुख्य समस्या",
  csProblemPh: (n: number) => `समस्या ${n}`,
  csAddProblem: "+ और जोड़ें",
  csRemove: "हटाएँ",
  csSuggestion: "मजबूती के लिए सुझाव",
  csLocation: "लोकेशन (केवल डोर टु डोर)",
  csGpsFinding: "लोकेशन ले रहे हैं…",
  csGpsOk: (m: string) => `📍 लोकेशन ली (±${m} मीटर)`,
  csGpsNone: "लोकेशन नहीं मिली — वॉर रूम में यह दिखेगा",
  csGpsDenied: "फोन ने लोकेशन की अनुमति नहीं दी — सेटिंग में अनुमति दें",
  csGpsAgain: "फिर से लें",
  csRequired: "यह भरना ज़रूरी है",
  csMobileBad: "10 अंकों का मोबाइल नंबर लिखें (6–9 से शुरू)",
  csAgeBad: "उम्र 15 से 120 के बीच लिखें",
  csSubmit: "सम्मिट ✓",
  csSaved: (name: string) => `सर्वे दर्ज हुआ — ${name}`,
  csSavedQueued: (name: string) => `सर्वे फोन में सेव — ${name} (नेटवर्क आते ही भेज देंगे)`,
  csAnother: "दूसरा सर्वे",
  csPending: (n: number) => `भेजना बाकी: ${n}`,
  csMine: "मेरा रिकॉर्ड",
  csToday: "आज",
  csTotal: "कुल",
  csBooths: "बूथ",
  csRecent: "पिछले सर्वे",
  csNoneYet: "अभी कोई सर्वे नहीं।",
  csMineOffline: "रिकॉर्ड देखने के लिए नेटवर्क चाहिए।",
  csFlag: (f: string): string =>
    f === "no_gps" ? "GPS नहीं" : f === "far_from_booth" ? "बूथ से दूर" : f === "one_spot" ? "एक ही जगह से" : f === "repeat_mobile" ? "नंबर पहले से" : f,
  csPrivacy: "🔒 नाम-नंबर केवल चुनाव प्रबंधन के लिए; किसी से साझा न करें।",
};

const fieldEn: { [K in keyof typeof fieldHi]: (typeof fieldHi)[K] } = {
  appName: "Election Manage Engine",
  appBy: "N-PAC · the key to your political success",
  loginId: "Email or mobile",
  loginIdHint: "e.g. name@npac.in or 98XXXXXXXX",
  loginWrong: "Wrong email / mobile or password",
  loginDisabled: "This login has been disabled. Contact your admin.",
  loginInvalid: "Enter a valid email or a 10-digit mobile number",
  loginForAssembly: (name) => `Assembly: ${name}`,
  loginAutoAssembly: "Your assembly and your work open by themselves once you log in.",
  otpTitle: "Enter the code",
  otpSentTo: (to, channel) => `A 6-digit code was sent by ${channel} to ${to} (valid 5 minutes).`,
  otpCode: "Code",
  otpVerify: "Verify code →",
  otpResend: "Resend the code",
  otpResendIn: (s) => `Resend (${s} s)`,
  otpDev: "dev (local)",
  otpDevHint: "Local server — code:",
  logoutOther: "Log out",
  linkMismatch: (link, own) => `This link is for ${link}, but your login is for ${own}. Opened ${own}.`,
  sessionInfo: "A login lasts 48 hours — log in the day before polling.",

  tabBooths: "Booths",
  tabPolling: "Polling day",
  tabSync: "Sync",
  changeAssembly: "Change assembly",
  showAll: "Show all booths",
  showMine: "Only my booths",
  oneTab: "The app is already open in another tab / window. Use that one, or close it and reload this page.",
  storageWarn: "Could not save to phone storage. Free some space; do not close the app.",
  privateMode: "This browser cannot keep data (private / incognito mode?). Open it in a normal window.",
  updateReady: "A new version of the app is ready",
  updateNow: "Update",
  offlineBar: "Offline — everything is being saved on the phone",

  installTitle: "Add the app to your home screen",
  installWhy: "Once added it opens like an app, even without network.",
  installAndroid: "Android (Chrome): ⋮ menu at the top → “Add to Home screen” or “Install app”.",
  installIphone: "iPhone (Safari): Share button □↑ at the bottom → “Add to Home Screen”.",
  installButton: "Install app",
  installDismiss: "Later / got it",

  locNeedsHttps: "Open the app from its https:// link to use location.",
  locUnavailable: "GPS is not available on this phone / browser.",
  locPermissionWeb: "Location permission was not given. In the browser tap 🔒 → Site settings → Location → Allow, then tap “Search again”.",

  sosTitle: "SOS — urgent help",
  sosBooth: "Booth",
  sosCategory: "What is happening?",
  cat_fake_voting: "Fake voting",
  cat_evm_fault: "EVM fault",
  cat_violence: "Violence",
  cat_other: "Other",
  sosNote: "Short note (optional)",
  sosSend: "Send SOS",
  sosSending: "Sending…",
  sosSent: (id, time) => `✓ SOS sent (#${id}, ${time})`,
  sosSentDup: "✓ Already received by the war room",
  sosWaiting: "⏳ Waiting for network — it will be sent automatically",
  sosFailed: "✗ SOS not sent:",
  sosNew: "New SOS",
  sosGps: (m) => `📍 GPS attached (±${m} m)`,
  sosGpsNone: "📍 No GPS fix — the SOS is sent anyway",
  sosChooseCategory: "First choose what is happening",
  sosByIdLabel: "ID",
  smsButton: "Send by SMS",
  smsExplain: "Your SMS app opens with the message filled in — you must press Send there. The app also sends it when the network is back (it is not counted twice).",
  recentSos: "Recent SOS",
  boothNotAssigned: (code) => `Booth ${code} is not assigned to you`,
  boothUnknown: (code) => `Booth ${code} is not in the list`,

  turnoutTitle: "Turnout",
  turnoutSlot: "Time",
  votesPolled: "Votes polled so far",
  turnoutSend: "Send turnout",
  turnoutPct: (p) => `≈ ${p}% turnout`,
  turnoutTotal: (n) => `Total voters: ${n}`,
  turnoutNoTotal: "Total voters not known yet (fill section 4) — the votes are saved, the % appears later",
  turnoutBadNumber: "Enter a valid number of votes",
  turnoutQueued: "Saved on the phone — will be sent when there is network",
  turnoutSent: "✓ Sent",
  turnoutToday: "Sent today",
  kindSos: "SOS",
  kindTurnout: "Turnout",

  mySosTitle: "My SOS — status in the war room",
  mySosEmpty: "No SOS from you in the last 3 days",
  mySosAsOf: (time) => `Status as of ${time}`,
  mySosOffline: "No network — showing the last known status",
  sosSt_open: "Open — not seen by the war room yet",
  sosSt_acknowledged: "Seen — being handled",
  sosSt_resolved: "Resolved",
  sosSt_waiting: "On the phone — sent when there is network",
  sosAckBy: (who, when) => `Seen by ${who}, ${when}`,
  sosResolvedBy: (who, when) => `Resolved by ${who}, ${when}`,
  sosResolutionNote: (note) => `Note: ${note}`,
  sosRaisedAt: (when) => `Sent ${when}`,
  sosLocationIgnored: (boothKnown) =>
    `Your phone's GPS position was outside India / unusable, so it was left out. The SOS arrived${boothKnown ? " — the booth's saved location is used instead." : "; the war room will locate it by the booth number."}`,
  mySosShowAll: (n) => `Show all ${n}`,
  turnoutWarnNoTotal: "Votes saved, but no %: the booth's total voters are not filled in yet (section 4). The % appears once they are.",
  turnoutWarnOverTotal: "Votes saved, but no %: more votes than the total voters. Check the total in section 4.",
  turnoutOverTotalLocal: (n) => `Note: this is more than the total voters (${n}). It will still be sent — check the total in section 4.`,

  modeSurveyor: "Surveyor login",
  modeWorker: "Polling day — worker / agent",
  ddayTitle: "Polling-day login",
  ddayExplain: "Opened from the war room's link — no number or passcode needed. Just pick your booth.",
  ddayNoLink: "For polling day, open the link the war room sent you (on WhatsApp) — it opens your booth.",
  ddayHow: "Making the link: web dashboard → War room → “Polling-day link” → “Make a new link” → send on WhatsApp. The worker taps it and this screen opens by itself — no password.",
  ddayPasteLabel: "Or paste the war room's link here",
  ddayPasteOpen: "Open link",
  ddayPasteBad: "This is not a polling-day link — paste the full link from the war room (it has dday=1).",
  drvExit: "Leave this vehicle (remove the link)",
  drvExitHelp: "End the duty, then leave — the phone can then open another vehicle's link or your own login.",
  drvExitConfirm: "Remove the vehicle's link from this phone? (End a running duty first)",
  modeDriver: "Driver",
  drvPasteTitle: "Campaign vehicle's driver",
  drvPasteHow: "Paste the link from the system (Campaign vehicles › vehicle › new driver link) and save — the duty screen opens and GPS recording starts.",
  drvPasteLabel: "Driver link",
  drvPasteOpen: "Save and start",
  drvPasteBad: "This is not a driver link — paste the full link made from the vehicle's record (it has drive=).",
  ddaySurveyorHint: "Surveyor / admin: log in with your ID and password under “Surveyor login” — the “Polling day” button is at the bottom.",
  ddayPickBooth: "Pick your booth",
  ddaySearch: "Type the booth number or name",
  ddayNoMatch: "No booth with this number / name",
  ddayMore: (n) => `${n} more booths — type the number above`,
  ddayName: "Your name (optional)",
  ddayMobileOpt: "Mobile number (optional)",
  ddayMobileBad: "Enter a 10-digit mobile number, or leave it empty",
  ddayStart: (code) => `Start for booth ${code}`,
  ddayLoadingBooths: "Loading the booth list…",
  ddayChange: "Change",
  ddayRetry: "Try again",
  ddayOpening: "Polling day — SOS",
  ddayOpeningSub: "Opening…",
  ddayAssembly: "Assembly code",
  ddayAssemblyHint: "e.g. HAR_01 (it is in the war room's link)",
  ddayMobile: "Your mobile number",
  ddayPasscode: "Polling-day passcode",
  ddayLogin: "Log in",
  ddayClosed: "This polling-day link is not valid any more. Ask the war room for the new link.",
  ddayNotOnList: "This number is not on any booth's worker or agent list. Talk to your booth president.",
  ddayWrong: "Wrong passcode",
  workerMyBooths: "Your booths",
  workerAnyBooth: "Type the booth number below — SOS can be sent for any booth of the assembly.",
  workerHello: (name) => `Hello ${name} — if anything goes wrong at a booth, send an SOS from here. The reply shows below.`,
  photoAdd: "📷 Add a photo (optional)",
  photoChange: "Change photo",
  photoRemove: "Remove photo",
  photoBusy: "Making the photo smaller…",
  photoFailed: "This photo could not be added. Pick another or send without one.",
  photoAttached: (kb) => `Photo attached (${kb} KB)`,
  ourVotesLabel: "Our party's votes (estimate, optional)",
  ourVotesHint: "The agent's own count; not more than the votes polled",
  ourVotesBad: "Our votes cannot be more than the votes polled",
  ourVotesPct: (pct) => `Our share of votes polled ${pct}%`,
  addProblem: "Add another problem",
  addSuggestion: "Add another idea",
  secSentiment: "6. Booth standing — surveyor's estimate (%)",
  secSentimentHint: "This is the booth's overall estimate (the three add to 100%). Fill the per-person current survey (name, mobile, problems, suggestion) in the \"Survey\" tab below.",
  svOpenPerson: "👤 Fill the per-person current survey",
  secResult: "Past election (booth votes)",
  list_workers: "7. Party workers list",
  list_influencers: "8. Influential / thoughtful persons",
  list_public: "9. Active citizens list",
  namesCount: (n) => (n ? `${n} names` : "No names yet"),
  unsentNames: "Saved on the phone — sent when there is network",
  myDuties: "Your duties",
  noBoothDuty: "None of your duties is booth work. Open “Campaign” below.",
  pAdd: "＋ Add a name",
  pName: "Name *",
  pRole: "Role *",
  pCaste: "Caste",
  pMobile: "Mobile number *",
  pLeaning: "Political leaning *",
  pNote: "Who they are",
  pNotePh: "e.g. retired teacher, village head",
  lean_supporter: "Our supporter",
  lean_inclined: "Leaning to us",
  lean_neutral: "Neutral",
  lean_opponent: "Opposed",
  pSaveNew: "Add to the list",
  pSaveEdit: "Save changes",
  pCancel: "Cancel",
  pEdit: "Edit",
  pRemove: "Remove",
  pConfirmRemove: (name) => `Remove ${name} from the list?`,
  pSearch: "Search by name or number",
  pEmpty: "No names yet. Tap “Add a name” above.",
  pDup: "This mobile number is already on this list",
  pFix: "Fix the red fields",
  pSavedOnline: "✓ Saved on the server",
  pSavedOffline: "✓ Saved on the phone — sent when there is network",
  pNotPrinted: "Any number of names. They are not printed in the booth book; the web exports them to Excel.",
  pShown: (shown, all) => `Showing ${shown} of ${all}`,
  pShowMore: "Show more",
  svExplain: "Which way the booth's people lean — from a phone or ground survey. The three must add up to 100%.",
  svStrong: "Strong (with us) %",
  svNeutral: "Neutral %",
  svWeak: "Needs more work %",
  svSample: "People spoken to",
  svMethod: "Survey method",
  m_call: "Phone",
  m_ground: "Ground (door to door)",
  m_mixed: "Both",
  svDate: "Survey date",
  svSum: (n) => `Total: ${n}%`,
  svSumBad: "The three must add up to 100%",
  svSave: "Save survey",
  aoNote: "What is already saved is not shown on the phone — only counts. You can add new entries here; viewing and correcting happens on the web dashboard.",
  aoOnServer: "Already on the server",
  aoFilled: "✓ Already filled",
  aoEmpty: "Empty",
  aoFilledShort: "filled",
  aoProblems: "Problems",
  aoSuggestions: "Suggestions",
  aoFilledField: (label) => `${label}: ✓ already filled`,
  aoNothingNew: "Fill in something new — what is already filled need not be sent again",
  aoAdded: (added, skipped) => `✓ ${added} added` + (skipped ? ` · ${skipped} already there, unchanged` : ""),
  aoMembersLabel: "members on the server",
  aoNewMember: (i) => `New member ${i}`,
  aoMembersFull: "All 20 members are filled. Changes on the web dashboard.",
  aoLines: (label, n) => `${label}: ${n} on the server`,
  aoLinesFull: "All 10 filled",
  aoDemoLocked: "Voter details are already filled. Corrections on the web dashboard.",
  aoCastes: (n) => `Castes: ${n}`,
  aoPeopleLabel: "names on the server",
  aoPending: (n) => `${n} names on the phone — sent when there is network`,
  aoAddedName: (name) => `✓ Added “${name}”`,
  aoSurveyDone: "✓ This booth's survey is already filled",
  aoSurveyNew: "Fill a new survey",
  aoSurveyNewNote: "A new survey replaces the old one.",
  aoResultDone: "✓ This booth's past result is already filled. Corrections on the web dashboard.",
  aoNamesCount: (n) => (n ? `${n} names` : "No names yet"),
  drvOpening: "Opening the vehicle's driver link…",
  drvClosed: "This driver link no longer works. Ask for the new link.",
  drvNoLink: "Drivers: open your vehicle's link (sent from the vehicles page).",
  drvTitle: "Vehicle duty",
  drvCandidate: "Candidate's car",
  drvStart: "▶ Start duty",
  drvEnd: "■ End duty",
  drvEndConfirm: "End the duty? GPS stops being recorded.",
  drvOn: "On duty",
  drvOff: "Off duty",
  drvSince: (t) => `since ${t}`,
  drvKm: "km (this duty)",
  drvSent: "GPS points sent",
  drvQueued: (n) => `${n} locations on the phone — sent when there is network`,
  drvGps: (acc, ago) => `GPS ±${acc} m · ${ago}`,
  drvWaitingGps: "Looking for GPS… stay under open sky",
  drvDenied: "Location permission was refused. Phone settings → browser / app → Location → Allow, then open this page again.",
  drvUnavailable: "No GPS on this phone. Turn on location (GPS).",
  drvKeepOpen: "Keep this app open and the screen on during the duty — many phones stop GPS when the screen is off. Keep the phone charging.",
  drvPrivacy: "GPS is recorded only during a duty — the route, the km and which markets / chowks were reached. Ending the duty stops tracking.",
  drvToday: (km, places, booths) => `Today: ${km} km · ${places} places · ${booths} booths`,
  drvPlan: "Places to reach today",
  drvPlanNone: "No route fixed for today — go where you are sent.",
  drvReached: "Reached",
  drvJustReached: (name) => `✓ Reached: ${name}`,
  drvScreenOn: "Screen kept on",
  drvBackgroundOk: "GPS runs in the background — you may lock the phone (a notification stays on top). Keep the phone charging and mobile data on.",
  drvNotifTitle: "Campaign vehicle — on duty",
  drvNotifText: "GPS is being recorded. Open the app to end the duty.",
  drvDeniedApp: "Location permission was not given. Choose 'Allow all the time' in settings, then start the duty again.",
  drvOpenSettings: "Open settings",
  drvMin: (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`),
  resExplain: (year) => `Votes per candidate in this booth in ${year} (from the Form-20).`,
  resOthers: "Others (remaining candidates)",
  resTotal: "Total",
  resSave: "Save votes",
  resSaved: "✓ Saved on the server",
  tabExit: "Exit poll",
  epTitle: "Exit poll survey",
  epRules: "Ask a voter who has voted only if they are willing. Take no name or phone. Do not tell anyone the results or post them on social media / WhatsApp — an offence under s. 126A.",
  epSavedNow: (n: number) => `${n} saved now`,
  epToday: (n: number) => `${n} on the server today`,
  epQueued: (n: number) => `${n} answers waiting on the phone — sent when online`,
  epAllSent: "✓ all sent",
  epNoPlan: "The war room has not set the method yet — these are starting choices.",
  epBooth: "Booth surveyed",
  epPickBooth: "Choose the booth",
  epChoice: "Who did you vote for?",
  epAge: "Age",
  epAges: { a18_25: "18-25", a26_40: "26-40", a41_60: "41-60", a60plus: "60+" } as Record<string, string>,
  epGender: "Gender",
  epGenders: { M: "Man", F: "Woman", O: "Other" } as Record<string, string>,
  epMore: "More questions (optional)",
  epPrev: "Who did you vote for last time?",
  epFirstTime: "First-time voter",
  epIssue: "Biggest issue",
  epMethod: "How asked",
  epMethods: { face: "Face to face", phone: "On the phone" } as Record<string, string>,
  epSave: "Save — next voter",
  epNeedBooth: "Choose the booth first",
  epGps: (m: number) => `📍 location ok (±${m} m)`,
  epNoGps: "📍 no location — the answer is still saved",
  bsTitle: "Settlements and crowded places",
  bsCardSub: "Slum / Dalit / tribal settlements, dense areas, markets, chowks — with address and population",
  bsHelp: "Add every settlement and crowded place of this booth. The most populated one shows in yellow ★.",
  bsAdd: "+ Add a settlement / place",
  bsNone: "No settlement or place added yet.",
  bsKind: "Kind of place",
  bsName: "Name",
  bsNamePh: "e.g. Kachchi Basti, Harijan Tola, Old Mohalla",
  bsAddress: "Address",
  bsAddressPh: "Lane / landmark — e.g. near the railway line",
  bsPopulation: "Approx. population (people)",
  bsHouseholds: "About how many houses",
  bsNumber: "Numbers only",
  bsGps: "Location",
  bsGpsAuto: "📍 Current location taken",
  bsGpsFinding: "Finding location…",
  bsGpsNone: "No location — you can add it later",
  bsGpsTake: "📍 Take this location",
  bsGpsClear: "Remove",
  bsSave: "Save",
  bsSaved: "✓ Saved",
  bsCancel: "Cancel",
  bsEdit: "Change",
  bsDensest: "★ Most populated",
  bsPeople: (n: string) => `~${n} people`,
  bsHouses: (n: string) => `${n} houses`,
  bsKinds: { slum: "Slum / poor settlement", dalit: "Dalit settlement", tribal: "Tribal settlement", dense: "Densely populated area", village: "Village / mohalla", market: "Market", chowk: "Chowk", crowd: "Crowded spot", ground: "Ground", auditorium: "Auditorium", hall: "Hall", other: "Other" } as Record<string, string>,
  resNeedsNet: "This part saves straight to the server — it needs network.",
  resNoElection: "No past election added yet. Ask the admin to make the candidate list in “Data import” on the web.",
  resNothing: "Enter the votes of at least one candidate",
  mpPoints: (n) => `Your points this week: ${n.toLocaleString("en-IN")}`,
  mpRank: (r, of) => `Place ${r} of ${of} in the team`,
  mpStart: "Save work to earn points",
  tabPlaces: "All places",
  tabTour: "Tour",
  tabRallies: "Rally / nukkad",
  tabVehicles: "Vehicles",
  vStaff: "Staff — work and data",
  vDriverOpt: "Driver — paste the link",
  vDriverConfirm: "This phone will now run in the vehicle's driver mode (this login signs out). Continue?",
  vDriverNote: "Do this on the driver's phone: on save the phone goes to that vehicle's duty screen and GPS recording starts. The link is made from the vehicle's record (web › Campaign vehicles › vehicle › new driver link).",
  apTitle: "Assembly's all places",
  apExplain: "Every booth's markets, chowks, crowded spots and settlements — with GPS. Tap a booth to add or change a place.",
  apSummary: (n, gps, b, all) => `${n} places · ${gps} with GPS · in ${b} / ${all} booths`,
  apSearch: "Booth no., booth or place name",
  apAdd: "Add a place — booth no.",
  apOpen: "Open",
  apNoBooth: "No booth with this number",
  apNoGps: "no GPS",
  apOnlyNoGps: (n) => `Only places still without GPS (${n}) — go there and tap “take location here”`,
  apEmptyBooths: (n) => `Booths without places (${n})`,
  apNone: "No place found",
  tabCampaign: "Campaign",
  campTitle: "Campaign",
  campExplain: "The campaign work of your duties. This part needs network.",
  camp_rallies: "Rally, nukkad sabha / natak management",
  camp_tour: "Candidate tour management",
  camp_vehicles: "Campaign vehicle management",
  campSub_rallies: "Permission, arrangements, crowd and cost",
  campSub_tour: "Today's stops — done / delayed / skipped",
  campSub_vehicles: "Each vehicle's route today, km, fuel",
  netNeeded: "No network. Open again when there is network.",
  reload: "Reload",
  saved: "✓ Saved",
  save: "Save",
  required: "This is required",
  boothCodeBad: "Write booth numbers like 12 or 45A",
  phoneBad: "Enter a 10-digit mobile number",
  rUpcoming: "Upcoming",
  rSearch: "🔍 Find a programme — name, place, booth, panchayat, guest",
  rNoMatch: "No programme matches.",
  rPast: "Held",
  rNew: "＋ New event",
  rEdit: "Edit event",
  rTitle: "Event title *",
  rKind: "Type",
  rDate: "Date *",
  rTime: "Time *",
  rPlace: "Place *",
  rBooth: "Booth no. (if any)",
  rExpected: "Expected crowd",
  rActual: "Actual crowd",
  rCost: "Cost (₹)",
  rIncharge: "In-charge",
  rInchargePhone: "In-charge's mobile",
  rStatus: "Status",
  rChecklist: "Arrangements",
  rChecklistN: (d, n) => `Arrangements ${d} / ${n}`,
  rNotes: "Notes",
  rEmpty: "No events in this list.",
  tDay: "Day",
  tToday: "Today",
  tAdd: "＋ Add a stop",
  tEdit: "Edit stop",
  tPlace: "Place / village *",
  tTime: "Time",
  tSeq: "Order",
  tBooth: "Booth no.",
  tPurpose: "Purpose",
  tContact: "Local contact",
  tContactPhone: "Contact's mobile",
  tEmpty: "No stops on this day.",
  tRoute: "Route on Google Maps",
  vEmpty: "No vehicles yet — the admin adds them on the web.",
  vToday: "Today's route",
  vNoTrip: "Today's route not filled",
  vSetTrip: "Fill today's route",
  vEditTrip: "Edit route",
  vArea: "Area / villages *",
  vBooths: "Booth numbers",
  vBoothsHint: "Separated by commas: 12, 13, 45A",
  vStartKm: "Morning reading (km)",
  vEndKm: "Evening reading (km)",
  vKmBad: "The evening reading can't be below the morning one",
  vFuelL: "Fuel (litres)",
  vFuelCost: "Fuel ₹",
  vCash: "Cash given to the driver ₹",
  vCashHint: "Cash handed over today (diesel / expenses)",
  vDriver: "Driver",
  vKm: (n) => `${n} km`,

  tabVoted: "Voted",
  vtTitle: "Voted — polling day",
  vtExplain: "Type the serial no. of each voter who has voted, or scan the slip's QR. Below are the supporters who have not come yet — fetch them from home.",
  vtPrivacy: "🔒 These names are only your booth's, only to bring supporters out to vote. Don't share them or take screenshots — everything is recorded.",
  vtOathTitle: "Oath",
  vtOathWhy: "Agree every 12 hours before the voter list's names open.",
  vtOathAgree: "I agree",
  vtBoothPick: "Your booth",
  vtBoothNo: "Booth no.",
  vtNoBooth: "No booth is assigned to you — talk to the admin.",
  vtVoted: "Voted",
  vtSupporters: "Supporters voted",
  vtPendingN: "Supporters left",
  vtSerial: "Voter's serial no. (from the part list)",
  vtSerialPh: "e.g. 245",
  vtMark: "Voted ✓",
  vtScan: "📷 Scan the slip's QR",
  vtScanClose: "Stop scanning",
  atTitle: "Attendance (QR) — ID-card scanner",
  atHelp: "Pick the programme, then scan the worker's ID-card QR or type the mobile number — the name, booth and running count show at once. (Needs internet.)",
  atEvent: "Programme",
  atKind: (k) => (k === "rally" ? "Rally / nukkad" : k === "tour" ? "Candidate's tour (today)" : "Meeting / other"),
  atNoRallies: "No rally listed — add one on the web, or pick \"Meeting / other\" and write a name.",
  atTourToday: (d) => `Attendance of today's (${d}) tour`,
  atMeetingName: "Meeting name",
  atMeetingPh: "e.g. Booth presidents' meeting, 5 Nov",
  atSoFar: "present so far",
  atPickEvent: "Pick the programme first",
  atScan: "📷 Scan the ID card's QR",
  atPhone: "Or type the mobile number",
  atName: "Name (not on any list — add)",
  atMark: "Mark present",
  atMarked: (n) => `✓ ${n} — marked present`,
  atAlready: (n) => `${n} is already marked`,
  atNotRegistered: "This number is on no booth's list — write the name to mark them.",
  atList: "Marked today (newest first)",
  rvTitle: "Review all work",
  rvOpenFull: "Open full screen",
  rvNoAssembly: "Choose the assembly.",
  tabAttendance: "Attendance",
  tabReview: "Review",
  tabHome: "Home",
  hmHello: (n) => (n ? `${n}, your work:` : "Your work:"),
  hmCount: (n) => `${n} sections`,
  hmBooths: "Booth details, team, status",
  hmSurvey: "Door-to-door / phone opinion survey",
  hmVehicles: "Staff: km, diesel, cash · driver: link",
  hmPolling: "SOS, turnout %, the day's report",
  hmReview: "The whole dashboard — view only",
  hmExit: "Ask voters after they vote",
  hmSync: "Send the phone's data to the server",
  hmAttendance: "Worker ID scan",
  hmAttendanceSub: "ID-card QR — attendance at rallies / meetings",
  hmPlaces: "Every place in the assembly",
  hmVoted: "Who voted at the booth",
  hmCounting: "Counting rounds",
  hmNote: "Only the work given to you in the admin panel shows here.",
  hmLocked: "Locked for now",
  hmLockedNote: "This module is not open for this assembly yet. Contact N-PAC to open it.",
  hmLockedOk: "OK",
  vtScanHint: "Hold the slip's QR code in front of the camera",
  vtCameraNo: "The camera did not open. Allow the camera in the browser, or type the serial no.",
  vtCameraUnsupported: "This phone / browser can't scan QR codes — type the serial no.",
  vtMarked: (serial, name, supporter) => `No. ${serial} — ${name}${supporter ? " (supporter)" : ""} ✓`,
  vtAlready: (serial, name) => `No. ${serial} — ${name}: already marked`,
  vtUnknown: (serial) => `No. ${serial} is not in this booth's list — check again`,
  vtQueued: (serial) => `No. ${serial} saved on the phone — sent as soon as there is network`,
  vtUnsent: (n) => `${n} marks still to send — they go by themselves when the network is back`,
  vtSendNow: "Send now",
  vtUndo: "↶ Undo the last mark",
  vtUndone: (serial) => `Mark on no. ${serial} removed`,
  vtUndoNeedsNet: "Undoing a mark needs network",
  vtSerialBad: "Type a serial no. between 1 and 100000",
  vtNotSlip: "This is not one of our voter slips' QR codes",
  vtOtherAssembly: (a) => `This slip is from another assembly (${a})`,
  vtOtherBooth: (b) => `This slip is for booth ${b}, not this booth`,
  vtPendingTitle: "Supporters who have not come yet",
  vtPendingNone: "Every supporter has voted 🎉",
  vtNoSupporters: "No supporters marked in this booth yet — mark them in the voter list on the web.",
  vtCame: "Came ✓",
  vtHouse: "House",
  vtRecent: "Just marked",
  vtNoVoters: "This booth's voter list is not uploaded yet — upload it on the web.",
  vtVia: (v) => (v === "qr" ? "QR" : v === "list" ? "list" : v === "web" ? "web" : "serial"),
  vtNeedsNet: "Names need network. Keep marking anyway — marks are saved on the phone.",

  tabCounting: "Counting",
  ctTitle: "Counting — booth votes",
  ctExplain: "Write each candidate's votes as the EVM shows them, booth by booth, with the round. The war room sees the lead and the projection at once.",
  ctNoSetup: "The war room has not set up the candidates yet — open again in a while.",
  ctDate: (d) => `Counting: ${d}`,
  ctDone: (n, all) => `${n} / ${all} booths sent`,
  ctBooth: "Booth",
  ctBoothPick: "Pick the booth",
  ctRound: "Round no.",
  ctRoundHint: (n) => (n ? `1 to ${n}` : "The round this booth was counted in"),
  ctRoundBad: (n) => (n ? `Round must be 1 to ${n}` : "Write a valid round no."),
  ctVotes: "Each candidate's votes (0 if none)",
  ctTotal: "Total votes",
  ctSave: "Send votes",
  ctSaved: (code, total, corrected) => `Booth ${code}: votes ${corrected ? "corrected" : "sent"} ✓ (total ${total})`,
  ctEntered: (round, time) => `This booth's votes were sent${round ? ` (round ${round})` : ""} · ${time}`,
  ctResend: "Need to change the figures? Send again",
  ctConfirm: "Sure? The figures sent earlier will be replaced (the old ones stay on record).",
  ctConfirmYes: "Yes, replace",
  ctCancel: "Cancel",
  ctAllRequired: "Write every candidate's votes (0 is fine)",
  ctRoundShort: (n) => `R${n}`,

  tabSurvey: "Survey",
  csTitle: "Current survey",
  csExplain: "Talk to each person and fill one form — it joins your record as soon as it is submitted.",
  csMethod: "Survey method",
  csD2d: "Door to door",
  csTele: "Telecaller",
  csBooth: "Booth no. and name",
  csBoothNo: "Booth number",
  csNoBooth: "No booth is assigned to you yet — ask your admin.",
  csPlace: "Mohalla / village",
  csPlacePick: "— pick —",
  csPlaceOther: "Other — type the name",
  csPlaceName: "Type the mohalla / village name",
  csPlaceNone: "No village / mohalla of this booth is listed yet — type the name (or add it under “All places”).",
  csPlaceOffline: "No network for the list — type the name.",
  csName: "Name *",
  csMobile: "Mobile number *",
  csAge: "Age",
  csProfession: "Profession",
  csOrgRole: "Political / social organisation role",
  csStatus: "Candidate's standing *",
  csStatusPick: "— pick —",
  csStrong: "Strong position",
  csNeutral: "Fair position",
  csWeak: "Needs more work",
  csProblems: "Main problem",
  csProblemPh: (n) => `Problem ${n}`,
  csAddProblem: "+ Add more",
  csRemove: "Remove",
  csSuggestion: "Suggestion to strengthen",
  csLocation: "Location (door to door only)",
  csGpsFinding: "Finding location…",
  csGpsOk: (m) => `📍 Location taken (±${m} m)`,
  csGpsNone: "No location — the war room will see this",
  csGpsDenied: "The phone refused location — allow it in settings",
  csGpsAgain: "Take again",
  csRequired: "Required",
  csMobileBad: "Write a 10-digit mobile number (starting 6–9)",
  csAgeBad: "Age must be 15 to 120",
  csSubmit: "Submit ✓",
  csSaved: (name) => `Survey saved — ${name}`,
  csSavedQueued: (name) => `Saved on the phone — ${name} (sent as soon as there is network)`,
  csAnother: "Next survey",
  csPending: (n) => `Waiting to send: ${n}`,
  csMine: "My record",
  csToday: "Today",
  csTotal: "Total",
  csBooths: "Booths",
  csRecent: "Recent surveys",
  csNoneYet: "No surveys yet.",
  csMineOffline: "The record needs network.",
  csFlag: (f) => (f === "no_gps" ? "no GPS" : f === "far_from_booth" ? "far from booth" : f === "one_spot" ? "one spot" : f === "repeat_mobile" ? "number repeated" : f),
  csPrivacy: "🔒 Names and numbers are for election management only; share them with no one.",
};

const hi = { ...MOBILE.hi, ...fieldHi };
export type Strings = typeof hi;
const en: Strings = { ...MOBILE.en, ...fieldEn };

export type Lang = "hi" | "en";
export const STRINGS: Record<Lang, Strings> = { hi, en };

export const categoryLabel = (t: Strings, c: SosCategory) => t[`cat_${c}` as const];

/**
 * Field errors come from @npac/rules / zod in English. Show the common ones in Hindi; anything
 * unknown is shown as is. (Better: messages in both languages in the rules package itself.)
 */
const HI_ERRORS: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/at least 1 character/i, () => "यह भरना ज़रूरी है"],
  [/Mobile number must be 10 digits/i, () => "मोबाइल नंबर 10 अंकों का हो और 6, 7, 8 या 9 से शुरू हो"],
  [/at most (\d+) character/i, (m) => `ज़्यादा से ज़्यादा ${m[1]} अक्षर`],
  [/Expected number, received nan/i, () => "सिर्फ अंक लिखें"],
  [/Required/i, () => "यह भरना ज़रूरी है"],
  [/less than or equal to (\d+)/i, (m) => `${m[1]} से ज़्यादा नहीं हो सकता`],
  [/greater than or equal to (\d+)/i, (m) => `${m[1]} से कम नहीं हो सकता`],
  [/Expected integer/i, () => "पूरी संख्या लिखें"],
  [/Each caste can appear once/i, () => "हर जाति एक बार ही लिखें"],
  [/Each slot .* once/i, () => "हर क्रमांक एक बार ही इस्तेमाल करें"],
];

export function localizeError(message: string, lang: Lang): string {
  if (lang !== "hi") return message;
  for (const [re, f] of HI_ERRORS) {
    const m = message.match(re);
    if (m) return f(m);
  }
  return message;
}
